import '../i18n/autoTranslate';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Eye, Download, CheckCircle, X, ExternalLink, FileDown } from 'lucide-react';
import SpotlightCard from '../../components/ui/SpotlightCard';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://ai-interview-back-node.onrender.com';

const toAbsoluteUrl = (maybeRelativeUrl) => {
  if (!maybeRelativeUrl) return '';
  if (/^https?:\/\//i.test(maybeRelativeUrl)) return maybeRelativeUrl;
  if (!maybeRelativeUrl.startsWith('/')) return `${API_BASE_URL}/${maybeRelativeUrl}`;
  return `${API_BASE_URL}${maybeRelativeUrl}`;
};

const getTemplateBackgroundImage = (category) => {
  switch (String(category || '').toLowerCase()) {
    case 'professional':
      return "/assets/images/company_xfhbif.png";
    case 'creative':
      return "/assets/images/global-network-connection_n9f8dc.png";
    case 'executive':
      return "/assets/images/executive.png";
    case 'traditional':
      return "/assets/images/university.png";
    default:
      return "/assets/images/interview_jnpdvt.png";
  }
};


// PDF Preview Modal Component using iframe
const PDFPreviewModal = ({ pdfUrl, isVisible, onClose, templateName, onDownloadDocx, isDownloading }) => {
  if (!isVisible || !pdfUrl) return null;

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="bg-[#0B0B12] rounded-3xl border border-white/10 max-w-7xl w-full h-[95vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 bg-white/5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)] flex-shrink-0">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-sm text-muted-foreground">PDF Preview</div>
              <div className="text-xl md:text-2xl font-bold text-foreground truncate">{templateName}</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => onDownloadDocx?.()}
              disabled={isDownloading}
              className="px-4 py-2 bg-emerald-600 text-white rounded-2xl font-semibold hover:bg-emerald-700 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="h-4 w-4" />
              <span>{isDownloading ? 'Preparing...' : 'Download DOCX'}</span>
            </button>
            <a
              href={pdfUrl}
              download={`${templateName || 'template'}.pdf`}
              className="px-4 py-2 bg-white/10 border border-white/10 text-white rounded-2xl font-semibold hover:bg-white/15 transition flex items-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>Download PDF</span>
            </a>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-white/5 border border-white/10 text-white/80 rounded-2xl font-semibold hover:bg-white/10 hover:text-white transition flex items-center space-x-2"
            >
              <ExternalLink className="h-4 w-4" />
              <span>Open in New Tab</span>
            </a>
            <button
              onClick={onClose}
              className="p-3 text-white/70 hover:text-white transition rounded-2xl hover:bg-white/10 border border-white/10"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* PDF Viewer using iframe */}
        <div className="flex-1 overflow-hidden bg-black">
          <iframe
            src={pdfUrl}
            className="w-full h-full border-0"
            title={`PDF Preview: ${templateName}`}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

// Template Card Component with embedded PDF preview using object tag
const TemplateCard = ({ template, isSelected, onSelect, onPreview, onDownloadDocx, isDownloading }) => {
  const [pdfError, setPdfError] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const backgroundImage = getTemplateBackgroundImage(template?.category);

  const handlePdfLoad = () => {
    setPdfLoaded(true);
    setPdfError(false);
  };

  const handlePdfError = () => {
    console.log('PDF failed to load:', template.previewUrl);
    setPdfError(true);
  };

  return (
    <button type="button" onClick={() => onSelect(template)} className="group text-left transition-all duration-200 h-full">
      <SpotlightCard
        className="custom-spotlight-card h-full rounded-3xl overflow-hidden"
        spotlightColor={isSelected ? "rgba(99, 102, 241, 0.26)" : "rgba(139, 92, 246, 0.10)"}
      >
        <div className={`
          relative h-full rounded-3xl border transition-all duration-200 overflow-hidden
          bg-white/5
          ${isSelected
            ? 'border-white/10 bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 shadow-[0_0_22px_rgba(99,102,241,0.22)]'
            : 'border-white/10 hover:border-purple-500/30 hover:shadow-[0_0_22px_rgba(99,102,241,0.12)]'
          }
        `}>
          <div className="relative flex flex-col h-full">
            <div className="relative aspect-[3/4] bg-[#0B0B12] overflow-hidden">
              <div className="absolute inset-0">
                <img
                  src={backgroundImage}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none"
                  onError={(e) => {
                    e.currentTarget.src = "/assets/images/no_image_jp3rlq.png";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/0 via-black/10 to-black/25" />
                {pdfError ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black/60 to-black">
                    <div className="text-center p-4">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-white/60" />
                      <p className="text-white/70 text-sm">PDF preview not available</p>
                      <p className="text-white/50 text-xs mt-1">Open full preview to view</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {!pdfLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-black/60 to-black z-10">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/60 mx-auto mb-3"></div>
                          <p className="text-white/60 text-sm">Loading preview...</p>
                        </div>
                      </div>
                    )}
                    <object
                      data={`${template.previewUrl}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                      type="application/pdf"
                      className="w-full h-full pointer-events-none"
                      onLoad={handlePdfLoad}
                      onError={handlePdfError}
                    >
                      <embed
                        src={`${template.previewUrl}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
                        type="application/pdf"
                        className="w-full h-full pointer-events-none"
                        onLoad={handlePdfLoad}
                        onError={handlePdfError}
                      />
                    </object>
                  </>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B12]/55 via-[#0B0B12]/10 to-transparent" />
              </div>

              <div className="absolute top-4 left-4 right-4 pointer-events-none">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{template.name}</div>
                    <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/75">
                      <span className="truncate">{template.category}</span>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-xs text-white">
                      <CheckCircle className="h-4 w-4" />
                      <span>Selected</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-200 bg-black/55">
                <div className="absolute inset-x-4 bottom-4 space-y-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreview(template);
                    }}
                    className="w-full px-4 py-3 bg-white text-black rounded-2xl font-semibold hover:bg-white/95 transition flex items-center justify-center space-x-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Full Screen Preview</span>
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownloadDocx?.(template);
                      }}
                      disabled={isDownloading}
                      className="px-3 py-2 bg-emerald-600 text-white rounded-2xl font-semibold hover:bg-emerald-700 transition flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileDown className="h-4 w-4" />
                      <span>{isDownloading ? '...' : 'DOCX'}</span>
                    </button>
                    <a
                      href={template.downloadUrl || template.previewUrl}
                      download={`${template.name}.pdf`}
                      onClick={(e) => e.stopPropagation()}
                      className="px-3 py-2 bg-white/10 border border-white/10 text-white rounded-2xl font-semibold hover:bg-white/15 transition flex items-center justify-center space-x-2"
                    >
                      <Download className="h-4 w-4" />
                      <span>PDF</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-white/10 bg-white/5">
              <p className="text-sm text-white/70 line-clamp-2">{template.description}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {template.tags?.slice(0, 2).map((tag, index) => (
                  <span
                    key={index}
                    className="px-2.5 py-1 text-xs rounded-full border bg-white/5 text-white/70 border-white/10"
                  >
                    {tag}
                  </span>
                ))}
                {template.tags && template.tags.length > 2 && (
                  <span className="px-2.5 py-1 text-xs rounded-full border bg-white/5 text-white/60 border-white/10">
                    +{template.tags.length - 2}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </SpotlightCard>
    </button>
  );
};

// Main Template Selection Component
const TemplateSelection = ({ selectedTemplate, onSelect, setAlertInfo }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const categories = ['All', 'Professional', 'Traditional', 'Creative', 'Executive'];

  useEffect(() => {
    const loadTemplates = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/templates`);
        if (!response.ok) throw new Error('Failed to load templates');
        const data = await response.json();

        const templatesWithUrls = (Array.isArray(data) ? data : []).map((t) => {
          const encodedId = encodeURIComponent(t.id);
          return {
            ...t,
            previewUrl: toAbsoluteUrl(`/api/templates/preview/${encodedId}`),
            downloadUrl: toAbsoluteUrl(`/api/templates/download/${encodedId}`),
            docxUrl: toAbsoluteUrl(`/api/templates/docx/${encodedId}`),
          };
        });

        setTemplates(templatesWithUrls);
        
        if (setAlertInfo) {
          setAlertInfo({
            variant: "default",
            title: "Templates Loaded",
            description: `Successfully loaded ${templatesWithUrls.length} PDF templates.`,
            show: true
          });
        }
      } catch (error) {
        console.error('Error loading templates:', error);
        if (setAlertInfo) {
          setAlertInfo({
            variant: "destructive",
            title: "Error Loading Templates",
            description: error.message,
            show: true
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [setAlertInfo]);

  const downloadDocxTemplate = async (template) => {
    if (!template?.docxUrl) return;
    try {
      setIsDownloading(true);
      const downloadLink = document.createElement('a');
      downloadLink.href = template.docxUrl;
      downloadLink.download = `${template.name || template.id}.docx`;
      downloadLink.target = '_blank';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      if (setAlertInfo) {
        setAlertInfo({
          variant: "default",
          title: "Download Started",
          description: `${template.name || template.id} DOCX template is downloading.`,
          show: true
        });
      }
    } catch (error) {
      console.error('DOCX download error:', error);
      if (setAlertInfo) {
        setAlertInfo({
          variant: "destructive",
          title: "Download Failed",
          description: `Failed to download DOCX template: ${error.message}`,
          show: true
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const filteredTemplates = selectedCategory === 'All' 
    ? templates 
    : templates.filter(template => template.category === selectedCategory);

  const handleTemplateSelect = (template) => {
    onSelect(template);
    if (setAlertInfo) {
      setAlertInfo({
        variant: "default",
        title: "Template Selected",
        description: `${template.name} template has been selected for your resume.`,
        show: true
      });
    }
  };

  const handlePreview = (template) => {
    console.log('Opening full screen preview for:', template);
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const handleDownloadDocx = (template) => {
    downloadDocxTemplate(template);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-3 mb-1">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Choose Template</h2>
          </div>
          <p className="text-muted-foreground">Loading available templates...</p>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/50"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center space-x-3 mb-1">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Choose Template</h2>
        </div>
        <p className="text-muted-foreground">
          Select a design that matches your industry and personal style
          {templates.length > 0 && ` (${templates.length} PDF templates available)`}
        </p>
        <p className="text-sm text-muted-foreground">
          DOCX versions are available for editing
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`
              px-4 py-2 rounded-2xl font-semibold transition-all duration-200 border
              ${selectedCategory === category
                ? 'bg-white/10 border-white/15 text-white'
                : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white'
              }
            `}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            isSelected={selectedTemplate?.id === template.id}
            onSelect={handleTemplateSelect}
            onPreview={handlePreview}
            onDownloadDocx={handleDownloadDocx}
            isDownloading={isDownloading}
          />
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <FileText className="mx-auto h-12 w-12 text-white/60 mb-4" />
          <p className="text-white/60">No templates found in this category.</p>
        </div>
      )}

      {/* Selection Summary */}
      {selectedTemplate && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle className="h-5 w-5 text-white/80" />
            <h3 className="font-semibold text-white">
              Template Selected: {selectedTemplate.name}
            </h3>
          </div>
          <p className="text-sm text-white/70">
            {selectedTemplate.description}
          </p>
          <button
            onClick={() => handleDownloadDocx(selectedTemplate)}
            disabled={isDownloading}
            className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-2xl font-semibold hover:bg-emerald-700 transition flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileDown className="h-4 w-4" />
            <span>{isDownloading ? 'Preparing...' : 'Download DOCX'}</span>
          </button>
        </div>
      )}

      {/* Full Screen PDF Preview Modal */}
      <PDFPreviewModal
        pdfUrl={previewTemplate?.previewUrl}
        isVisible={showPreview}
        onClose={() => setShowPreview(false)}
        templateName={previewTemplate?.name}
        onDownloadDocx={() => handleDownloadDocx(previewTemplate)}
        isDownloading={isDownloading}
      />
    </div>
  );
};

export default TemplateSelection;
